import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Monitor, ExternalLink, Copy, Circle, Search, Maximize, KeyRound, Lock, Building2, MapPin, Clock, AlertTriangle, PackageCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const STATUS = {
  online: { color: "text-emerald-500", label: "Online" },
  offline: { color: "text-slate-400", label: "Offline" },
  maintenance: { color: "text-amber-500", label: "Maintenance" },
};

function relTime(iso) {
  if (!iso) return "Never seen";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "Seen just now";
  if (m < 60) return `Seen ${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `Seen ${h}h ago`;
  return `Seen ${Math.floor(h / 24)}d ago`;
}

export default function KioskTerminal() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState("");
  const [pinModal, setPinModal] = useState(null); // {id, name, pin_set}
  const [pinValue, setPinValue] = useState("");
  const [pinAudit, setPinAudit] = useState([]);

  const load = () => {
    api.get("/hierarchy/tree").then((r) => {
      const g = [];
      r.data.forEach((org) => {
        (org.buildings || []).forEach((b) => {
          const locations = [];
          (b.locations || []).forEach((l) => {
            if ((l.kiosks || []).length) locations.push({ id: l.id, name: l.name, kiosks: l.kiosks });
          });
          if (locations.length) g.push({ key: b.id, orgName: org.name, buildingName: b.name, locations });
        });
      });
      setGroups(g);
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const launchFullscreen = async (code) => {
    try {
      if (document.documentElement.requestFullscreen) await document.documentElement.requestFullscreen();
    } catch { /* fullscreen may be blocked; continue locked anyway */ }
    navigate(`/kiosk/${code}?locked=1`);
  };
  const preview = (code) => window.open(`/kiosk/${code}`, "_blank", "noopener");
  const copyLink = async (code) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/kiosk/${code}`);
      toast.success("Kiosk link copied");
    } catch { toast.error("Could not copy link"); }
  };

  const openPin = (k) => {
    setPinValue("");
    setPinAudit([]);
    setPinModal({ id: k.id, name: k.name, pin_set: k.pin_set });
    api.get(`/kiosks/${k.id}/pin-audit`).then((r) => setPinAudit(r.data)).catch(() => {});
  };
  const savePin = async (clear = false) => {
    const pin = clear ? "" : pinValue.trim();
    if (!clear && !/^\d{4,6}$/.test(pin)) { toast.error("PIN must be 4–6 digits"); return; }
    try {
      await api.put(`/kiosks/${pinModal.id}/pin`, { pin });
      toast.success(clear ? "PIN removed" : "PIN set");
      setPinModal(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const ql = q.toLowerCase();
  const match = (k) => k.name.toLowerCase().includes(ql) || (k.code || "").toLowerCase().includes(ql);
  const filtered = groups
    .map((g) => ({
      ...g,
      locations: g.locations.map((l) => ({ ...l, kiosks: l.kiosks.filter(match) })).filter((l) => l.kiosks.length),
    }))
    .filter((g) => g.locations.length);

  const Card = ({ k }) => {
    const s = STATUS[k.status] || STATUS.offline;
    return (
      <div data-testid={`kiosk-terminal-card-${k.code}`} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-5 flex flex-col">
        <div className="flex items-start justify-between">
          <div className="h-11 w-11 rounded-xl bg-blue-50 dark:bg-slate-900 flex items-center justify-center"><Monitor className="h-5 w-5 text-blue-600" /></div>
          <span className="flex items-center gap-1 text-xs font-medium text-slate-500"><Circle className={`h-2 w-2 fill-current ${s.color}`} /> {s.label}</span>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <p className="font-semibold text-slate-900 dark:text-white">{k.name}</p>
          {k.pin_set && <span data-testid={`kiosk-terminal-pin-badge-${k.code}`} className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-600 bg-amber-50 dark:bg-amber-950/40 px-1.5 py-0.5 rounded"><Lock className="h-3 w-3" /> PIN</span>}
        </div>
        <p className="text-xs text-slate-400">{k.code}</p>
        <div className="mt-3 flex items-center gap-4 text-xs">
          <span data-testid={`kiosk-terminal-lastseen-${k.code}`} className="flex items-center gap-1 text-slate-500"><Clock className="h-3.5 w-3.5" /> {relTime(k.last_seen)}</span>
          {k.low_stock > 0 ? (
            <span data-testid={`kiosk-terminal-lowstock-${k.code}`} className="flex items-center gap-1 font-medium text-amber-600"><AlertTriangle className="h-3.5 w-3.5" /> {k.low_stock} low stock</span>
          ) : (
            <span data-testid={`kiosk-terminal-lowstock-${k.code}`} className="flex items-center gap-1 text-emerald-600"><PackageCheck className="h-3.5 w-3.5" /> Stock OK</span>
          )}
        </div>
        <div className="mt-4 flex gap-2">
          <Button data-testid={`kiosk-terminal-launch-${k.code}`} onClick={() => launchFullscreen(k.code)} className="flex-1 bg-blue-600 hover:bg-blue-700"><Maximize className="h-4 w-4 mr-1.5" /> Launch fullscreen</Button>
          <Button data-testid={`kiosk-terminal-setpin-${k.code}`} variant="outline" title="Set pairing PIN" onClick={() => openPin(k)}><KeyRound className="h-4 w-4" /></Button>
          <Button data-testid={`kiosk-terminal-copy-${k.code}`} variant="outline" title="Copy link" onClick={() => copyLink(k.code)}><Copy className="h-4 w-4" /></Button>
        </div>
        <button data-testid={`kiosk-terminal-preview-${k.code}`} onClick={() => preview(k.code)} className="mt-2 inline-flex items-center gap-1 text-xs text-slate-400 hover:text-blue-600 transition-colors">
          <ExternalLink className="h-3 w-3" /> Preview in new tab (unlocked)
        </button>
      </div>
    );
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-admin-head text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">Kiosk Terminal</h1>
          <p className="text-slate-500 mt-1">Launch the full-screen customer terminal for any kiosk on this device.</p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="kiosk-terminal-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search kiosk name or code" className="pl-9" />
        </div>
      </div>

      {filtered.map((g) => {
        const total = g.locations.reduce((n, l) => n + l.kiosks.length, 0);
        return (
          <div key={g.key} data-testid={`kiosk-terminal-group-${g.key}`} className="space-y-4">
            <div className="flex items-center gap-2 pt-2 border-b border-slate-200 dark:border-slate-800 pb-2">
              <Building2 className="h-4 w-4 text-blue-600" />
              <h2 className="text-sm font-bold uppercase tracking-wide text-slate-700 dark:text-slate-200">{g.buildingName}</h2>
              <span className="text-xs text-slate-400">· {g.orgName}</span>
              <span className="text-xs text-slate-400">· {total} kiosk{total > 1 ? "s" : ""}</span>
            </div>
            {g.locations.map((l) => (
              <div key={l.id} data-testid={`kiosk-terminal-location-${l.id}`} className="space-y-3 pl-1">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500">
                  <MapPin className="h-3.5 w-3.5" /> {l.name}
                </div>
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {l.kiosks.map((k) => <Card key={k.id} k={k} />)}
                </div>
              </div>
            ))}
          </div>
        );
      })}
      {!filtered.length && <p className="text-sm text-slate-400">No kiosks found.</p>}

      <Dialog open={!!pinModal} onOpenChange={(o) => !o && setPinModal(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Pairing PIN — {pinModal?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-1">Staff must enter this PIN on the tablet to activate the kiosk.</p>
          <div>
            <Label>New PIN (4–6 digits)</Label>
            <Input
              data-testid="kiosk-pin-input"
              value={pinValue}
              onChange={(e) => setPinValue(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              placeholder="e.g. 1234"
              className="mt-1 tracking-[0.4em] text-lg"
            />
          </div>
          {pinAudit.length > 0 && (
            <div className="border-t border-slate-200 dark:border-slate-800 pt-3" data-testid="kiosk-pin-audit">
              <p className="text-xs font-semibold text-slate-500 mb-1.5">Change history</p>
              <ul className="space-y-1 max-h-32 overflow-y-auto">
                {pinAudit.map((a) => (
                  <li key={a.id} className="text-xs text-slate-500 flex items-center justify-between gap-2">
                    <span><span className="capitalize font-medium text-slate-700 dark:text-slate-300">PIN {a.action}</span> · {a.by_name}</span>
                    <span className="text-slate-400 whitespace-nowrap">{new Date(a.at).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <DialogFooter className="gap-2 sm:justify-between">
            {pinModal?.pin_set
              ? <Button data-testid="kiosk-pin-remove" variant="outline" className="text-rose-600 border-rose-200" onClick={() => savePin(true)}>Remove PIN</Button>
              : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setPinModal(null)}>Cancel</Button>
              <Button data-testid="kiosk-pin-save" onClick={() => savePin(false)} className="bg-blue-600 hover:bg-blue-700">Save PIN</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
